<template>
	<div>
		<div class="flex filter_table">
			<div class="flex flex_column w_100">
				<ag-item
					:rowData="rowData"
					:columnDefs="columnDefs"
					v-on:sortChanged="sortChanged"
					:loading-warning-text="loadingWarningText"
					:key="loadingWarningText"
					:with-button-group="true"
					:with-tabs="true"
				></ag-item>
				<Pagination
					:totalNum="totalNum"
					:pageSize="pageSize"
					:sizeChange="sizeChange"
					:currentChange="currentChange"
					:currentPage="page"
				></Pagination>
			</div>
			<InvestorPaymentsRevolvingPutOptionsFilter @apply-filter="applyFilter"/>
		</div>
		<RevolvingPutOptionPremiumPaymentsPlanModal ref="premiumPaymentsModal"/>
	</div>
</template>
<script>

import {asDecimal, asLocalizedDate, asMoneyAmount} from "@/components/templates/agGrid/formatter/valueFormatters";
import AgItem from "@/components/templates/agGrid/AgItem";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";
import InvestorPaymentsRevolvingPutOptionsFilter
	from "@/components/templates/investor/payments/revolving/InvestorPaymentsRevolvingPutOptionsFilter";
import {revolvingPutOptionsBuyerApi} from "@/common/api/revolvingPutOptionsBuyerApi";
import RevolvingPutOptionPremiumPaymentsPlanModal
	from "@/components/templates/modal/revolvingPutOption/RevolvingPutOptionPremiumPaymentsPlanModal";

export default {
    extends: PaginationParent,
    components: {
		RevolvingPutOptionPremiumPaymentsPlanModal,
        AgItem,
		InvestorPaymentsRevolvingPutOptionsFilter,
        Pagination
    },
    props: {
        accountId: { type: Number, default: -1 },
        accountsAssigned: { type: Boolean }
    },
    data() {
        return {
            rowData: null,
            columnDefs: [],
            filters: [],
            sorting: [],
        };
    },
    computed: {
      loadingWarningText() {
          return !this.accountsAssigned ? this.$t('common.noAccountsAssigned') : null
      }
    },
    beforeMount() {
        this.setColumnDefs();
        this.getList();
    },
    methods: {
        sortChanged(sortData) {
            this.sorting = sortData;
            this.initPage();
            this.getList();
        },
        getList() {
            // Early out if the account is not yet set, showing an empty table
            if (this.accountId < 0) {
                return;
            }

            let postData = {
                columns: [],
                filters: this.filters || [],
                maxResults: this.maxResults,
                offset: this.offset,
                sorting: this.sorting,
            };

            revolvingPutOptionsBuyerApi.inspection.listPayments(this.accountId, postData)
                .then(res => {
                    this.setTotalNum(res.recordsTotal);
                    this.rowData = res.data;
                })
                .catch(this.toast);
        },
        setColumnDefs() {
            this.columnDefs = [
				{
					headerName: this.$t("common.debtor"),
					field: "debtorName",
					nameType: "debtorName",
					idType: "debtorId",
					minWidth: 175,
					cellRendererFramework: "CompanyTem",
					sortable: true,
				},
				{
					headerName: this.$t("common.supplier"),
					field: "sellerName",
					minWidth: 175,
					nameType: "sellerName",
					idType: "sellerId",
					cellRendererFramework: "CompanyTem",
					sortable: true
				},
				{
					headerName: this.$t("common.contractNumber"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.contractNumber.tooltip"),
					},
					field: "referenceNumber",
					minWidth: 175,
					cellRendererFramework: "RevolvingPutOptionReferenceRenderer",
					cellRendererParams: {
						soldReceivablesFunc: (data, postData) => revolvingPutOptionsBuyerApi.inspection.listReceivables(data.offerId, postData)
					},
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.optionStart"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.optionStart.tooltip"),
					},
					minWidth: 120,
					field: "startDate",
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.optionEnd"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.optionEnd.tooltip"),
					},
					minWidth: 120,
					field: "endDate",
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.revolvingLine"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.revolvingLine.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 150,
					field: "saleLimit",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asMoneyAmount(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.estimatedSalesVolume"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.estimatedSalesVolume.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 215,
					field: "estimatedSalesVolume",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asMoneyAmount(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.premium"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.premium.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 150,
					field: "premium",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.premiumPayment.nextDueOn"),
					minWidth: 160,
					field: "nextPremiumPaymentDueDate",
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("common.amount"),
					headerClass: "align-right",
					minWidth: 120,
					field: "totalPremiumAmount",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asMoneyAmount(),
					sortable: true
				},
				{
					headerName: this.$t("common.outstanding"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.premiumPayment.outstanding.tooltip")
					},
					headerClass: "align-right",
					field: "outstandingPremiumAmount",
					cellStyle: {'justify-content': 'flex-end'},
					minWidth: 125,
					valueFormatter: asMoneyAmount({ percentValueField: "outstandingPremiumPercent" }),
					sortable: true
				},
				{
					headerName: this.$t("common.actions"),
					minWidth: 100,
					cellRendererFramework: "InvestorPaymentsRevolvingPutOptionsTableOperation",
					cellRendererParams: {
						showPaymentPlan: this.showPaymentPlan
					}
				}
            ];
        },
        applyFilter(filters) {
            this.filters = filters;
            this.getList();
        },
		showPaymentPlan(data) {
        	this.$refs.premiumPaymentsModal.showWithData(
        		data.referenceNumber,
				revolvingPutOptionsBuyerApi.inspection.getPaymentPlan(data.offerId)
			);
		}
    },
    watch: {
        accountId() {
            this.getList();
        }
    }
};
</script>
